import { Button, Space, Table, Typography } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteCompetition } from "~entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "~hooks/useGetOrgData";
import { useHandlePagination } from "~hooks/useHandlePagination";
import SearchFilter from "~components/SearchFilter/SearchFilter.js";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";

const PermissionName = PermissionNames.COMPETITION;
const columnsone = ({customerId, sport, organizationId, formatMessage, handleOpenConfirmation}) => {
  return [
    {
      dataIndex: "images",
      render: (image) => {
        return (
          <div style={{float: "left", height: "60px", width: "60px"}} className="inline-image-container">
            {image.length > 0 && <img src={image[0].url} alt="LOGO" className="inline-image"/>}
          </div>
        );
      },
      width: 1,
    },
    {
      title: formatMessage({
        id: "competition.title",
        defaultMessage: "Competition Title",
      }),
      dataIndex: "nameLocal",
    },
    {
      title: formatMessage({
        id: "abbreviationLocal",
        defaultMessage: "Abbreviation",
      }),
      dataIndex: "abbreviationLocal",
    },
    {
      title: formatMessage({
        id: "ageGroup",
      }),
      dataIndex: "ageGroup",
    },
    {
      align: "right",
      render: (_, record) => (
        <div style={{display: "flex"}}>
          <PermissionCheck permissionKeys={[PermissionNames.SEASON]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/competition/${record.competitionId}/seasons/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "seasons",
                  defaultMessage: "Seasons",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/competition/manage/${record.competitionId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`}/>
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const CompetitionList = () => {
  const {formatMessage} = useIntl();
  const {sport, organizationId, customerId} = useGetOrgData();
  const [selectedCompetition, setSelectedCompetition] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {mutate: deleteFn, isPending} = useDeleteCompetition();
  const [searchText, setSearchText] = useState("");

  const {
    pageSize,
    currentPage,
    handlePaginationChange,
    pageSizeOptions
  } = useHandlePagination(50, 1, [50, 100, 500, 1000]);

  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { scope, resources } = scopeData || {};

  const allCompetitions = useMemo(() => {
    const competitionResources = resources?.competitions || {};
    const competitionsList = [];

    scope?.leagueTree?.forEach(({ competitions }) => {
      competitionsList.push(...competitions.map(({ competitionId }) => competitionResources[competitionId]));
    });

    return competitionsList;
  }, [scope?.leagueTree, resources?.competitions]);

  const filteredCompetitions = useMemo(
    () => allCompetitions.filter(({ nameLocal }) => !searchText || nameLocal?.toLowerCase().includes(searchText.toLowerCase())),
    [allCompetitions, searchText],
  );

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelectedCompetition(record);
      setShowConfirmation(true);
    },
    [setSelectedCompetition, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selectedCompetition?.competitionId);
  }, [deleteFn, selectedCompetition]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className={"mb-2"}
      >
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title={formatMessage({ id: "competition.delete"})}
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelectedCompetition({});
            }}
            okText={formatMessage({id: 'delete'})}
          >
            <div>
              {formatMessage(
                {
                  id: "delete.confirm",
                },
                { item: selectedCompetition.nameLocal },
              )}
            </div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>{formatMessage({id: "competitions"})}</Typography.Title>
        <Space>
          <SearchFilter
            doSearch={setSearchText}
            placeholderIntlKey="search.two.letters"
          />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/competition/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
               {formatMessage({id: "competition"})}
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </div>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey={"competitionId"}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: filteredCompetitions.length,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
        dataSource={filteredCompetitions}
      />
    </>
  );
};

export default CompetitionList;
